import React, { useState, useEffect } from 'react';
import Sidebar from '../components/Sidebar';
import { Card, CardBody, Row, Col, Input, Button, Spinner, Badge, Alert } from 'reactstrap';
import axios from 'axios';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const AppConfig = ({ onLogout }) => {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.junctionverse.com';
  
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  
  // Form state for version configs
  const [formData, setFormData] = useState({
    min_app_version: '',
    latest_version: '',
    force_update: 'false',
    update_message: '',
    play_store_url: '',
    app_store_url: '',
  });

  // Fetch configs on mount
  useEffect(() => {
    fetchConfigs();
  }, []);

  const fetchConfigs = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(`${API_BASE_URL}/api/app/configs`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      setConfigs(response.data);
      
      // Populate form with existing values
      const configMap = {};
      response.data.forEach(config => {
        configMap[config.key] = config.value;
      });
      
      setFormData({
        min_app_version: configMap['min_app_version'] || '1.0.0',
        latest_version: configMap['latest_version'] || '1.0.0',
        force_update: configMap['force_update'] || 'false',
        update_message: configMap['update_message'] || 'A new version is available with bug fixes and new features!',
        play_store_url: configMap['play_store_url'] || '',
        app_store_url: configMap['app_store_url'] || '',
      });
      
      setError(null);
    } catch (err) {
      console.error('Failed to fetch configs:', err);
      setError('Failed to load configurations. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (key, value) => {
    setFormData(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const saveConfig = async (key, value, description) => {
    try {
      const token = localStorage.getItem('accessToken');
      await axios.post(`${API_BASE_URL}/api/app/config`, {
        key,
        value,
        description
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      return true;
    } catch (err) {
      console.error(`Failed to save ${key}:`, err);
      return false;
    }
  };

  const handleSaveAll = async () => {
    setSaving(true);
    
    const configsToSave = [
      { key: 'min_app_version', value: formData.min_app_version, description: 'Minimum app version required (force update below this)' },
      { key: 'latest_version', value: formData.latest_version, description: 'Latest available app version' },
      { key: 'force_update', value: formData.force_update, description: 'Whether to force users to update (true/false)' },
      { key: 'update_message', value: formData.update_message, description: 'Message shown in update dialog' },
      { key: 'play_store_url', value: formData.play_store_url, description: 'Google Play Store URL' },
      { key: 'app_store_url', value: formData.app_store_url, description: 'Apple App Store URL' },
    ];

    let allSuccess = true;
    for (const config of configsToSave) {
      const success = await saveConfig(config.key, config.value, config.description);
      if (!success) allSuccess = false;
    }

    setSaving(false);
    
    if (allSuccess) {
      toast.success('All configurations saved successfully!');
      fetchConfigs(); // Refresh
    } else {
      toast.error('Some configurations failed to save. Please try again.');
    }
  };

  const isValidVersion = (version) => {
    return /^\d+\.\d+\.\d+$/.test(version);
  };

  const compareVersions = (v1, v2) => {
    const parts1 = v1.split('.').map(Number);
    const parts2 = v2.split('.').map(Number);
    for (let i = 0; i < 3; i++) {
      if (parts1[i] > parts2[i]) return 1;
      if (parts1[i] < parts2[i]) return -1;
    }
    return 0;
  };

  const versionWarning = () => {
    if (!isValidVersion(formData.min_app_version) || !isValidVersion(formData.latest_version)) {
      return null;
    }
    if (compareVersions(formData.min_app_version, formData.latest_version) > 0) {
      return 'Warning: Minimum version is higher than latest version!';
    }
    return null;
  };

  if (loading) {
    return (
      <div className='d-flex w-100 overflow-hidden'>
        <div className='asidebar'>
          <Sidebar />
        </div>
        <div className='main-content d-flex justify-content-center align-items-center'>
          <Spinner color="primary" />
        </div>
      </div>
    );
  }

  return (
    <div className='d-flex w-100 overflow-hidden'>
      <ToastContainer position="top-right" autoClose={3000} />
      <div className='asidebar'>
        <Sidebar />
      </div>
      <div className='main-content'>
        <div className='d-flex align-items-center justify-content-between mb-4'>
          <div>
            <h2>App Configuration</h2>
            <p className="text-muted mb-0">Manage app version requirements and update settings</p>
          </div>
          <Button
            color="primary"
            onClick={handleSaveAll}
            disabled={saving}
            style={{
              height: '50px',
              fontWeight: '600',
              border: 'none',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              minWidth: '150px'
            }}
          >
            {saving ? <Spinner size="sm" /> : 'Save All Changes'}
          </Button>
        </div>

        {error && (
          <Alert color="danger" className="mb-4">
            {error}
          </Alert>
        )}

        {versionWarning() && (
          <Alert color="warning" className="mb-4">
            {versionWarning()}
          </Alert>
        )}

        <Row>
          {/* Version Settings Card */}
          <Col lg={6} className="mb-4">
            <Card className="h-100 shadow-sm">
              <CardBody>
                <h5 className="mb-4 d-flex align-items-center">
                  <span style={{ 
                    background: '#667eea', 
                    color: 'white', 
                    borderRadius: '8px',
                    padding: '8px 12px',
                    marginRight: '12px',
                    fontSize: '14px'
                  }}>
                    Version Control
                  </span>
                </h5>

                <div className="mb-4">
                  <label className="form-label fw-bold">
                    Minimum App Version
                    <Badge color="danger" className="ms-2">Required</Badge>
                  </label>
                  <Input
                    type="text"
                    value={formData.min_app_version}
                    onChange={(e) => handleInputChange('min_app_version', e.target.value)}
                    placeholder="e.g., 1.0.0"
                    className={!isValidVersion(formData.min_app_version) && formData.min_app_version ? 'is-invalid' : ''}
                  />
                  <small className="text-muted">
                    Users with app version below this will be prompted to update
                  </small>
                  {!isValidVersion(formData.min_app_version) && formData.min_app_version && (
                    <div className="invalid-feedback">Please use format: X.X.X (e.g., 1.2.3)</div>
                  )}
                </div>

                <div className="mb-4">
                  <label className="form-label fw-bold">
                    Latest App Version
                  </label>
                  <Input
                    type="text"
                    value={formData.latest_version}
                    onChange={(e) => handleInputChange('latest_version', e.target.value)}
                    placeholder="e.g., 1.1.0"
                    className={!isValidVersion(formData.latest_version) && formData.latest_version ? 'is-invalid' : ''}
                  />
                  <small className="text-muted">
                    The latest version available in app stores
                  </small>
                </div>

                <div className="mb-3">
                  <label className="form-label fw-bold">
                    Force Update
                  </label>
                  <div>
                    <div className="form-check form-check-inline">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="forceUpdate"
                        id="forceUpdateYes"
                        checked={formData.force_update === 'true'}
                        onChange={() => handleInputChange('force_update', 'true')}
                      />
                      <label className="form-check-label" htmlFor="forceUpdateYes">
                        Yes - Block app until updated
                      </label>
                    </div>
                    <div className="form-check form-check-inline">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="forceUpdate"
                        id="forceUpdateNo"
                        checked={formData.force_update === 'false'}
                        onChange={() => handleInputChange('force_update', 'false')}
                      />
                      <label className="form-check-label" htmlFor="forceUpdateNo">
                        No - Allow skip
                      </label>
                    </div>
                  </div>
                  <small className="text-muted d-block mt-2">
                    {formData.force_update === 'true' 
                      ? 'Users cannot use the app until they update' 
                      : 'Users can dismiss the update dialog and continue'}
                  </small>
                </div>
              </CardBody>
            </Card>
          </Col>

          {/* Update Message & Store URLs Card */}
          <Col lg={6} className="mb-4">
            <Card className="h-100 shadow-sm">
              <CardBody>
                <h5 className="mb-4 d-flex align-items-center">
                  <span style={{ 
                    background: '#ff6705', 
                    color: 'white', 
                    borderRadius: '8px',
                    padding: '8px 12px',
                    marginRight: '12px',
                    fontSize: '14px'
                  }}>
                    Update Dialog
                  </span>
                </h5>

                <div className="mb-4">
                  <label className="form-label fw-bold">Update Message</label>
                  <Input
                    type="textarea"
                    value={formData.update_message}
                    onChange={(e) => handleInputChange('update_message', e.target.value)}
                    placeholder="Message shown to users when an update is available"
                    rows={3}
                  />
                  <small className="text-muted">
                    This message is displayed in the update dialog
                  </small>
                </div>

                <div className="mb-4">
                  <label className="form-label fw-bold">
                    Play Store URL
                    <Badge color="secondary" className="ms-2">Android</Badge>
                  </label>
                  <Input
                    type="url"
                    value={formData.play_store_url}
                    onChange={(e) => handleInputChange('play_store_url', e.target.value)}
                    placeholder="https://play.google.com/store/apps/details?id=..."
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label fw-bold">
                    App Store URL
                    <Badge color="secondary" className="ms-2">iOS</Badge>
                  </label>
                  <Input
                    type="url"
                    value={formData.app_store_url}
                    onChange={(e) => handleInputChange('app_store_url', e.target.value)}
                    placeholder="https://apps.apple.com/app/..."
                  />
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Preview Card */}
        <Card className="shadow-sm">
          <CardBody>
            <h5 className="mb-4">
              <span style={{ 
                background: '#28a745', 
                color: 'white', 
                borderRadius: '8px',
                padding: '8px 12px',
                marginRight: '12px',
                fontSize: '14px'
              }}>
                Preview
              </span>
              Current Configuration
            </h5>
            
            <div className="bg-light rounded p-4">
              <Row>
                <Col md={3} className="mb-3 mb-md-0">
                  <div className="text-muted small">Minimum Version</div>
                  <div className="h5 mb-0 text-danger">{formData.min_app_version || '-'}</div>
                </Col>
                <Col md={3} className="mb-3 mb-md-0">
                  <div className="text-muted small">Latest Version</div>
                  <div className="h5 mb-0 text-success">{formData.latest_version || '-'}</div>
                </Col>
                <Col md={3} className="mb-3 mb-md-0">
                  <div className="text-muted small">Force Update</div>
                  <div className="h5 mb-0">
                    {formData.force_update === 'true' 
                      ? <Badge color="danger">Yes</Badge>
                      : <Badge color="secondary">No</Badge>
                    }
                  </div>
                </Col>
                <Col md={3}>
                  <div className="text-muted small">Last Updated</div>
                  <div className="h6 mb-0">
                    {configs.length > 0 
                      ? new Date(configs[0].updatedAt).toLocaleDateString()
                      : 'Never'
                    }
                  </div>
                </Col>
              </Row>
            </div>

            <div className="mt-4 p-3 border rounded bg-white">
              <strong>How it works:</strong>
              <ul className="mb-0 mt-2">
                <li>If user's app version &lt; <strong>Minimum Version</strong>: Update dialog shown</li>
                <li>If <strong>Force Update</strong> is enabled: User cannot dismiss dialog</li>
                <li>Clicking "Update Now" opens the respective app store</li>
              </ul>
            </div>
          </CardBody>
        </Card>
      </div>
    </div>
  );
};

export default AppConfig;
